import { useCallback } from 'react';
import { api } from '../../../api/client';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import {
  postSaveSdmsEdits, postValidate, type RollbackResponse, type SdmsCheckPointEdit,
} from '../SdmsValidationActions';

/**
 * The PLM form's Reject: the shared whole-batch rollback with `plmReject`, which makes the
 * server refuse a batch that is not Pending Validation (Status 2) — G5
 * PlmV2WorkspaceController.Reject_Execute :1206 — and write G5's audit row. The SDMS
 * rollback (no flag) is unchanged. Owner 2026-10-17, G5 parity.
 */
export async function postPlmReject(batchIds: string[], reason: string): Promise<RollbackResponse> {
  const res = await api.post('/api/v1/analysis/result-validation/rollback', {
    batchIds,
    reason,
    plmReject: true,
  });
  return res.data as RollbackResponse;
}

/** A batch as the confirm and the result messages name it. */
export interface PlmValidateTarget {
  /** SpreadSheetEntry_AnalyticalBatch.Oid. */
  id: string;
  /** The QC Batch ID shown to the user. */
  label: string;
}

/**
 * PLM Validate mode's two actions (prototype v13 `validateBatches`,
 * 2026-09-29) — ONE flow for the Validate list and the batch detail, so the two
 * can never behave differently.
 *
 * Both post to the shared Result Validation endpoints, but the server validates a
 * PLM batch EXACTLY as G5 PlmV2Store.ValidateBatch does (AnalysisWriter.PlmValidateSql
 * — owner 2026-10-17, G5 parity): Pending Validation only, check points answered,
 * no self-validation while "No Self-Review" is on, then the review step Default
 * Settings ask for. A refused batch comes back in `skipped` and is reported by name.
 */
/** G5's success wording (PlmV2Workspace / PlmV2BatchList, owner 2026-10-19): one batch, every ticked
 *  batch, or "<n> of <m> batches …" when some were refused. */
export const doneText = (done: number, total: number, verb: 'validated' | 'rejected'): string =>
  total <= 1 ? `Batch ${verb} successfully.`
    : done === total ? `Batches ${verb} successfully.` : `${done} of ${total} batches ${verb} successfully.`;

export function usePlmValidateActions() {
  const confirm = useConfirm();
  const notify = useNotify();

  /** Names each refused batch next to the server's reason. */
  const reportSkipped = useCallback((targets: PlmValidateTarget[], skipped: Record<string, string> | undefined) => {
    const lines = Object.entries(skipped ?? {})
      .map(([id, why]) => `${targets.find(t => t.id.toLowerCase() === id.toLowerCase())?.label ?? id}: ${why}`);
    if (lines.length > 0) notify.error(lines.join('\n'));
  }, [notify]);

  /**
   * Validate the batches. `checkPoints` are the detail's pending check-point
   * answers, saved first (as SDMS Result Validation does) so an answer given on
   * screen is never lost. Resolves true when at least one batch was validated.
   */
  const validate = useCallback(async (
    targets: PlmValidateTarget[], checkPoints?: SdmsCheckPointEdit[],
  ): Promise<boolean> => {
    if (targets.length === 0) return false;
    const names = targets.map(t => t.label).join(', ');
    const ok = await confirm.confirm({
      title: 'Validate',
      // G5 Validate_Execute :1191 (owner 2026-10-17, G5 parity).
      message: `Do you want to validate ${names}?`,
      confirmLabel: 'Validate',
    });
    if (!ok) return false;
    try {
      if (checkPoints && checkPoints.length > 0 && targets.length === 1) {
        await postSaveSdmsEdits(targets[0]!.id, { checkPoints });
      }
      const resp = await postValidate(targets.map(t => t.id), '');
      reportSkipped(targets, resp.skipped);
      if (resp.count > 0) notify.success(doneText(resp.count, targets.length, 'validated'));   // G5 :1196 / list :294
      return resp.count > 0;
    } catch {
      // The shared API client has already shown the server's message.
      return false;
    }
  }, [confirm, notify, reportSkipped]);

  /**
   * Reject = return the batches to the analyst (prototype v13): the SHARED
   * whole-batch rollback, which puts the batch back at Pending Submission with
   * its layers open for entry. The reason is required and audited. Only a batch
   * Pending Validation may be rejected (G5 Reject_Execute :1206) — the server
   * refuses the others and names them.
   */
  const reject = useCallback(async (targets: PlmValidateTarget[]): Promise<boolean> => {
    if (targets.length === 0) return false;
    const names = targets.map(t => t.label).join(', ');
    const reason = await confirm.prompt({
      title: 'Reject',
      // G5 Reject_Execute :1208 (owner 2026-10-17, G5 parity).
      message: `Do you want to reject ${names}? Reason (required — audited):`,
      placeholder: 'Reason (required — audited)',
      required: true,
      confirmLabel: 'Reject',
    });
    if (!reason?.trim()) return false;
    try {
      const resp = await postPlmReject(targets.map(t => t.id), reason.trim());
      reportSkipped(targets, resp.skipped);
      if (resp.count > 0) notify.success(doneText(resp.count, targets.length, 'rejected'));   // G5 :1211 / list :322
      return resp.count > 0;
    } catch {
      return false;
    }
  }, [confirm, notify, reportSkipped]);

  return { validate, reject };
}
